/**
 * True si el HTML no tiene contenido visible: '', solo tags vacíos (<br>, <p></p>),
 * &nbsp; o espacios. Se usa para colapsar sub-elementos (título/subtítulo/botón)
 * cuando el usuario los borra en el builder, en vez de dejar el hueco con su margen.
 */
export const isBlankHtml = (html?: string | null): boolean => {
  if (html == null) return true;
  const text = String(html)
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, '');
  return text.length === 0;
};

export const mapFuelTypeToSpanish = (
  fuelType: 'Gasoline' | 'Diesel' | 'Hybrid' | 'Electric' | 'Gas'
): string => {
  const fuelTypeMap = {
    Gasoline: 'Gasolina',
    Diesel: 'Diésel',
    Hybrid: 'Híbrido',
    Electric: 'Eléctrico',
    Gas: 'Gas',
  };

  return fuelTypeMap[fuelType];
};

export const mapTransmissionTypeToSpanish = (
  transmissionType: string
): string => {
  if (transmissionType?.toLowerCase()?.includes('auto')) {
    return 'Automático';
  }

  const transmissionTypeMap: { [key: string]: string } = {
    Automatic: 'Automático',
    Manual: 'Manual',
  };

  return transmissionTypeMap[transmissionType] || transmissionType;
};

export const mapConditionTypeToSpanish = (
  conditionType: 'New' | 'Used' | 'Certified Pre-Owned'
): string => {
  const conditionTypeMap = {
    New: 'Nuevo',
    Used: 'Usado',
    'Certified Pre-Owned': 'Certificado Pre-Vendido',
  };

  return conditionTypeMap[conditionType];
};

/** El país en una dirección de EE.UU. ("USA", "US", "United States", "EE. UU."…). */
const PAIS_US = /^(usa|u\.s\.a\.?|us|u\.s\.?|united states( of america)?|ee\.?\s?uu\.?|estados unidos)$/i;
/** La sigla de un estado suelta ("FL", "TX"): dos mayúsculas. */
const ESTADO_US = /^[A-Z]{2}$/;
/** Los estados por su nombre, por si la dirección no trae la sigla. */
const ESTADOS_US = new Set([
  'alabama', 'alaska', 'arizona', 'arkansas', 'california', 'colorado', 'connecticut', 'delaware',
  'district of columbia', 'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa',
  'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'michigan', 'minnesota',
  'mississippi', 'missouri', 'montana', 'nebraska', 'nevada', 'new hampshire', 'new jersey',
  'new mexico', 'new york', 'north carolina', 'north dakota', 'ohio', 'oklahoma', 'oregon',
  'pennsylvania', 'rhode island', 'south carolina', 'south dakota', 'tennessee', 'texas', 'utah',
  'vermont', 'virginia', 'washington', 'west virginia', 'wisconsin', 'wyoming', 'puerto rico',
]);

/**
 * Comuna/ciudad desde una dirección estilo Google Places
 * ("Prieto Sur 1073, Temuco, Región de La Araucanía 4780000, Chile" → "Temuco").
 * Es el último segmento sin dígitos, descartando país y región.
 */
const cityFromAddress = (address?: string | null, country?: string | null): string => {
  // Brasil: formato de Google "Av. Paulista, 1000 - Bela Vista, São Paulo - SP,
  // 01310-100, Brasil". Se descartan el país y la sigla del estado ("- SP"). La
  // rama de Chile, abajo, queda tal cual.
  if (isBrasil(country)) {
    const partes = (address || '')
      .split(',')
      .map((s) => s.trim())
      .filter((p) => p && !/^(brasil|brazil)$/i.test(p));
    for (let i = partes.length - 1; i >= 0; i--) {
      if (!/\d/.test(partes[i])) return partes[i].replace(/\s+-\s+[A-Z]{2}$/, '').trim();
    }
    return '';
  }
  // EE.UU.: formato de Google "742 Evergreen Terrace, Springfield, IL 62704, USA".
  // Además de lo que descarta Chile (abajo: una sucursal con dirección chilena
  // sigue dando la comuna), se descartan el país ("USA", "United States"…) y la
  // sigla del estado suelta ("IL"); "IL 62704" ya trae dígitos. Un estado escrito
  // entero ("Florida") se salta solo si antes viene la ciudad: "New York" o
  // "Washington" también son ciudades. La rama de Chile, abajo, queda tal cual.
  if (forcedSiteLanguage(country) === 'en') {
    const candidatas = (address || '')
      .split(',')
      .map((s) => s.trim())
      .filter((p) => p && !/\d/.test(p) && !/^chile$/i.test(p) && !/regi[oó]n|metropolitan/i.test(p))
      .filter((p) => !PAIS_US.test(p) && !ESTADO_US.test(p));
    const ultima = candidatas[candidatas.length - 1] ?? '';
    const anterior = candidatas[candidatas.length - 2];
    return ESTADOS_US.has(ultima.toLowerCase()) && anterior ? anterior : ultima;
  }
  const parts = (address || '')
    .split(',')
    .map((s) => s.trim())
    .filter((p) => p && !/^chile$/i.test(p) && !/regi[oó]n|metropolitan/i.test(p));
  for (let i = parts.length - 1; i >= 0; i--) {
    if (!/\d/.test(parts[i])) return parts[i];
  }
  return '';
};

/**
 * Etiqueta visible de una sucursal para el sitio público.
 * En la BD hay dos tipos de name: personalizados ("Riesco Motors Patronato"),
 * que se usan tal cual, y autogenerados ("Sucursal - <dirección completa>",
 * "Sucursal #4", "Sucursal Principal"), ilegibles como etiqueta — para esos
 * se deriva la comuna desde la dirección ("Temuco").
 */
export const dealershipLabel = (
  d?: { name?: string | null; address?: string | null } | null,
  /** País del tenant. Opcional: sin él (o CL) es exactamente lo de siempre. */
  country?: string | null
): string => {
  if (!d) return '';
  const name = (d.name || '').trim();
  const address = (d.address || '').trim();
  const isAutoName =
    !name ||
    /^sucursal(\s+principal)?(\s*#?\d*)?$/i.test(name) ||
    (!!address && name.toLowerCase().endsWith(address.toLowerCase()));
  if (!isAutoName) return name;
  return cityFromAddress(address, country) || name || address;
};

/**
 * Flag de sitio: ¿mostrar la ubicación (sucursal) de los autos en el sitio?
 * Se guarda en client_website_config.integrations.show_vehicle_location y se
 * edita desde el admin (Configuración → Sucursales). Default = mostrar (true):
 * ausente/indefinido = se muestra, para no cambiar el comportamiento de los
 * sitios existentes. Solo se oculta si el dueño lo apagó explícitamente.
 * Acepta también el string 'false' por si llegara serializado.
 */
export const showVehicleLocation = (
  client?: { client_website_config?: unknown } | null
): boolean => {
  const cfg = client?.client_website_config as
    | { integrations?: { show_vehicle_location?: boolean | string } }
    | Array<{ integrations?: { show_vehicle_location?: boolean | string } }>
    | undefined;
  const row = Array.isArray(cfg) ? cfg[0] : cfg;
  const val = row?.integrations?.show_vehicle_location;
  return val !== false && val !== 'false';
};

/**
 * ¿La ficha del auto ofrece el botón "Agendar visita"?
 *
 * Mismo mecanismo que `showVehicleLocation`, y a propósito: es la misma clase
 * de decisión —esconder un elemento de la ficha para UNA automotora— así que
 * vive en el mismo lugar, `client_website_config.integrations`, y no en una
 * columna nueva. `integrations` ya viaja en el select liviano del tenant, así
 * que esto no agrega ni una consulta ni una migración.
 *
 * ── Por qué apagarlo por tenant y no borrar el botón ───────────────────────
 *
 * Medido el 26-oct-2026 sobre las citas vivas: 41 entraron por este botón, en
 * 14 automotoras, contra 49 por el link general `/agendar`. Es casi la mitad
 * del agendamiento web. Borrarlo del código le quitaría a esas 14 un camino
 * que usan, sin que lo hayan pedido.
 *
 * ── Qué NO apaga ───────────────────────────────────────────────────────────
 *
 * El link `/agendar` y el ítem del menú siguen funcionando: son los que las
 * automotoras reparten por WhatsApp, y `/agendar` tiene candado para que nunca
 * deje de andar. Esto saca UNA de las tres puertas, no el agendamiento.
 *
 * ── El default ─────────────────────────────────────────────────────────────
 *
 * Ausente = `true`, igual que su hermana: un tenant que nunca tocó esto ve el
 * botón de siempre. También cubre al `client` del SSR, que llega sin
 * `client_website_config` — aunque en la práctica el botón no se pinta en el
 * servidor: espera a `hasAvailableSlots`, que lo resuelve una consulta del
 * navegador, y para entonces el store ya está hidratado. O sea que no hay
 * parpadeo en ninguna de las dos direcciones.
 */
export const showVehicleBooking = (
  client?: { client_website_config?: unknown } | null
): boolean => {
  const cfg = client?.client_website_config as
    | { integrations?: { vehicle_booking_enabled?: boolean | string } }
    | Array<{ integrations?: { vehicle_booking_enabled?: boolean | string } }>
    | undefined;
  const row = Array.isArray(cfg) ? cfg[0] : cfg;
  const val = row?.integrations?.vehicle_booking_enabled;
  return val !== false && val !== 'false';
};

import { buildWhatsAppUrl } from './contact-utils';
import { trackConversionFromHref } from '@/lib/track-site-event';
import { isBrasil, forcedSiteLanguage, phoneDigitsRule, type PhoneDigitsRule } from '@/lib/country';
import { normalizeTransmission } from '@/lib/vehicleSpecs';
import { catalogPt } from '@/i18n/locales/pt/catalog';
import { en as recursosEn } from '@/i18n/locales/en/index';

/**
 * Wrapper de compatibilidad. Si el número no es válido devuelve '#' para que el
 * `<a href>` no abra una conversación rota. Preferí `buildWhatsAppUrl` en código
 * nuevo: te devuelve `null` y podés decidir esconder el botón.
 */
export const contactByWhatsApp = (
  phone: string,
  message?: string,
  /** País del tenant. Opcional: sin él (o CL) es exactamente lo de siempre. */
  country?: string | null
): string => {
  return whatsAppUrlPorPais(phone, message, country) || '#';
};

/**
 * Teléfono brasileño → `55DDDNNNNNNNN` (12 o 13 dígitos, sin "+"), o `null`.
 * Acepta con o sin +55, con el 0 de larga distancia y con 00 internacional.
 */
const normalizarTelefonoBrasil = (raw?: string | null): string | null => {
  let d = String(raw ?? '').replace(/\D/g, '');
  if (d.startsWith('00')) d = d.slice(2);
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) return d;
  if (d.startsWith('0') && (d.length === 11 || d.length === 12)) d = d.slice(1);
  if (d.length === 10 || d.length === 11) return `55${d}`;
  return null;
};

/** Teléfono brasileño con "+55…", o `null`. Para mostrar o para pasarle a WhatsApp. */
export const telefonoBrasilConMas = (raw?: string | null): string | null => {
  const n = normalizarTelefonoBrasil(raw);
  return n ? `+${n}` : null;
};

/**
 * Dígitos internacionales (sin '+') de un teléfono guardado, según el PAÍS del
 * tenant, o `null` si no se reconoce. Es el récord `phoneDigitsRule` de
 * `lib/country` (CL / BR / CO / AR; desconocido → CL), con una salvedad: Brasil
 * conserva la regla histórica de ESTE módulo (`normalizarTelefonoBrasil`, que
 * además entiende el 0 de larga distancia nacional), así lo que ya se mostraba
 * en los sitios de Brasil no cambia. `propias` deja que una pantalla conserve su
 * propia variante para algún país, sin un `if` por país.
 */
export const digitosTelefono = (
  raw: string | null | undefined,
  country?: string | null,
  propias?: Partial<Record<string, PhoneDigitsRule>>
): string | null => {
  const d = String(raw ?? '').replace(/\D/g, '');
  return phoneDigitsRule(country, { BR: normalizarTelefonoBrasil, ...propias })(d);
};

/** Teléfono con '+' por país (`digitosTelefono`), o `null`. Para mostrar o para `tel:`. */
export const telefonoConMas = (
  raw: string | null | undefined,
  country?: string | null,
  propias?: Partial<Record<string, PhoneDigitsRule>>
): string | null => {
  const n = digitosTelefono(raw, country, propias);
  return n ? `+${n}` : null;
};

/**
 * `buildWhatsAppUrl` por país: acepta string, `{ phone }` o JSON-string y arma
 * `wa.me/<dígitos>` con la regla del país del tenant (`digitosTelefono`). Sin
 * país, o con uno desconocido, es exactamente la regla chilena de siempre; si
 * el número no se reconoce devuelve `null` (el botón se esconde).
 */
export const whatsAppUrlPorPais = (
  phone: string | { phone?: string | null } | null | undefined,
  message?: string,
  country?: string | null
): string | null => {
  let raw = '';
  if (typeof phone === 'string') {
    try {
      const parsed = JSON.parse(phone);
      raw = parsed?.phone || phone;
    } catch {
      raw = phone;
    }
  } else if (phone && typeof phone === 'object') {
    raw = phone.phone || '';
  }
  const normalized = digitosTelefono(String(raw), country);
  if (!normalized) return null;
  const encoded = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${normalized}${encoded}`;
};

// ── Valores de catálogo de la base (en español) → etiqueta en portugués ──

export type TipoValorCatalogo = 'condition' | 'fuel' | 'transmission' | 'category' | 'color';

/** "Semi-nuevo" → "semi_nuevo"; "Moto, ATV y UTV" → "moto_atv_y_utv". */
export const slugValorCatalogo = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * Etiqueta en portugués de un VALOR de catálogo que la base guarda en español
 * ("semi-nuevo" → "Seminovo", "gasolina" → "Gasolina", "Camioneta" →
 * "Caminhonete"), o `null` si no está en el mapa (el que llama muestra el valor
 * crudo). Sólo para MOSTRAR: los filtros, la URL y las comparaciones siguen
 * usando el valor de la base. Se llama SOLO en sitios de Brasil
 * (`isBrasil(country)`): un sitio chileno muestra el valor como siempre.
 */
export const etiquetaCatalogoPt = (
  tipo: TipoValorCatalogo,
  raw: unknown
): string | null => {
  const mapa = catalogPt.values[tipo] as Record<string, string>;
  const slug = tipo === 'transmission' ? normalizeTransmission(raw) : slugValorCatalogo(raw);
  // `hasOwn`: un valor como "constructor" no puede devolver lo del prototipo.
  return slug && Object.prototype.hasOwnProperty.call(mapa, slug) ? mapa[slug] : null;
};

/**
 * Lo mismo en inglés, SOLO para sitios de EE.UU. Sale de `catalog.values` del
 * i18n en inglés; si ese mapa no está (o no tiene el valor), `null` y el que
 * llama muestra el valor crudo, como en Brasil.
 */
export const etiquetaCatalogoEn = (
  tipo: TipoValorCatalogo,
  raw: unknown
): string | null => {
  const valores = (recursosEn as unknown as {
    catalog?: { values?: Partial<Record<TipoValorCatalogo, Record<string, string>>> };
  }).catalog?.values;
  const mapa = valores?.[tipo];
  if (!mapa) return null;
  const slug = tipo === 'transmission' ? normalizeTransmission(raw) : slugValorCatalogo(raw);
  return slug && Object.prototype.hasOwnProperty.call(mapa, slug) ? mapa[slug] : null;
};

/**
 * La etiqueta de un valor de catálogo en el idioma OBLIGATORIO del país: pt
 * (Brasil), en (EE.UU.); `null` para el resto (y para lo que no está en el mapa).
 */
export const etiquetaCatalogoDelPais = (
  tipo: TipoValorCatalogo,
  raw: unknown,
  country: string | null | undefined
): string | null => {
  const forzado = forcedSiteLanguage(country);
  if (forzado === 'pt') return etiquetaCatalogoPt(tipo, raw);
  if (forzado === 'en') return etiquetaCatalogoEn(tipo, raw);
  return null;
};

/**
 * Lo que se MUESTRA de un valor de catálogo: en Brasil la etiqueta en portugués
 * y en EE.UU. en inglés (o el valor crudo si no está en el mapa); en cualquier
 * otro país, `deSiempre`, que es lo que cada pantalla mostraba hasta hoy (con
 * su capitalización).
 */
export const etiquetaCatalogo = (
  tipo: TipoValorCatalogo,
  raw: unknown,
  country: string | null | undefined,
  deSiempre: string
): string => {
  if (!forcedSiteLanguage(country)) return deSiempre;
  return etiquetaCatalogoDelPais(tipo, raw, country) ?? deSiempre;
};

/**
 * Normalizes builder links: converts hash-based anchors like "#contact"
 * to proper routes like "/contact". Leaves absolute URLs and already
 * correct paths unchanged.
 */
export const normalizeBuilderLink = (link: string): string => {
  if (!link) return link;
  const v = link.trim();
  // Bloquear esquemas peligrosos (javascript:, data:, vbscript:, file:) que
  // un tenant podria poner en un link del builder y terminarian en un href
  // ejecutable -> XSS al hacer clic. Se quitan espacios/control para evitar
  // bypass tipo "java\\tscript:". (fix audit)
  const deScheme = v.replace(/[\u0000-\u0020]+/g, '').toLowerCase();
  if (/^(javascript|data|vbscript|file):/.test(deScheme)) return '#';
  if (/^[^\s@/#:]+@[^\s@/#:]+\.[^\s@/#:]+$/.test(v)) return `mailto:${v}`;
  // Already a proper path or external URL
  if (v.startsWith('/') || v.startsWith('http')) return v;
  // Convert #section to /section
  if (v.startsWith('#')) return `/${v.slice(1)}`;
  return v;
};

/**
 * Resuelve un link del builder distinguiendo internos vs externos.
 *
 * El bug que arregla: un link como `instagram.com/foo` o `www.cliente.cl`
 * (sin `https://`) lo tomaba el navegador / el <Link> de Next como ruta
 * RELATIVA, así que le pegaba el dominio de la web adelante
 * (`carssale.cl/instagram.com/foo`). Acá detectamos dominios "pelados" y los
 * tratamos como externos agregándoles `https://`.
 *
 * - `https://`, `http://`, `mailto:`, `tel:` → externo, tal cual.
 * - `//host` (protocol-relative) → externo con `https:`.
 * - `/ruta`, `#ancla` → interno (lo maneja el <Link> de Next).
 * - `dominio.tld/...` (tiene un punto antes de la primera `/`) → externo,
 *   se le antepone `https://`.
 * - cualquier otra cosa → interno bajo `/`.
 */
export const resolveNavLink = (
  raw?: string | null
): { href: string; isExternal: boolean } => {
  const link = (raw || '').trim();
  if (!link) return { href: '#', isExternal: false };
  if (/^(https?:\/\/|mailto:|tel:)/i.test(link)) return { href: link, isExternal: true };
  // Email suelto → correo (antes lo tomaba como dominio pelado: https://correo@…).
  if (/^[^\s@/#:]+@[^\s@/#:]+\.[^\s@/#:]+$/.test(link)) return { href: `mailto:${link}`, isExternal: true };
  if (link.startsWith('//')) return { href: `https:${link}`, isExternal: true };
  if (link.startsWith('/') || link.startsWith('#')) return { href: link, isExternal: false };
  // ¿dominio pelado? p.ej. "instagram.com/x" o "www.cliente.cl"
  const firstSegment = link.split(/[/?#]/)[0];
  if (firstSegment.includes('.')) return { href: `https://${link}`, isExternal: true };
  return { href: `/${link}`, isExternal: false };
};

/**
 * Navegación del botón primario de los Heros (típicamente "Ver vehículos").
 *
 * Bug que arregla: los Heros hacían scroll a CUALQUIER `[class*="vehicle"]`
 * de la página e IGNORABAN el link que el cliente ponía en el builder, así que
 * "/vehicles" o una URL completa nunca navegaban. Solo el `#ancla` debe hacer
 * scroll a una sección de la misma página; una ruta o URL real debe navegar.
 *
 * - `#seccion` → scroll suave a esa sección si existe (comportamiento legacy).
 * - `/ruta` → navega en la misma pestaña.
 * - `http…` / dominio pelado → navega en pestaña nueva.
 * - vacío → no hace nada y devuelve false (el Hero puede usar su fallback).
 *
 * Devuelve true si manejó el click. Llamar SOLO fuera del editor.
 */
export const navigateBuilderCta = (rawLink?: string | null): boolean => {
  const link = (rawLink || '').trim();
  if (!link) return false;
  // Ancla (#seccion): scroll a esa sección de la misma página.
  if (link.startsWith('#')) {
    const id = link.slice(1);
    const el =
      document.getElementById(id) ||
      document.querySelector(`[data-section="${id}"]`) ||
      document.querySelector('[data-section="vehicles"]') ||
      document.getElementById('vehicles-section') ||
      document.querySelector('[class*="vehicle"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }
  // Ruta interna o URL externa: navegar respetando el link del builder.
  const { href, isExternal } = resolveNavLink(link);
  // site_events: los Heros usan <button>, no <a>; si el CTA apunta a
  // WhatsApp/tel se registra acá (whatsapp_click / call_click), sin bloquear.
  trackConversionFromHref(href);
  if (isExternal) {
    window.open(href, '_blank');
  } else {
    window.location.href = href;
  }
  return true;
};
